// The date range the Calendar page asks the API for.
//
// Kept apart from the page for the same reason reportRange.ts is: this
// arithmetic is the only part of /calendar that can be tested without a DB,
// and it is the part that was wrong.
//
// THE INVARIANT THIS MODULE EXISTS TO HOLD: for a fixed set of controls, the
// returned range must not change as the clock ticks within a day. These values
// go straight into a TanStack query key, and query keys are identified by the
// HASH OF THEIR VALUES — so a range that drifts every render mints a brand-new
// query every render, which is permanently `isPending` and refetches forever.
// That is what left /calendar stuck on "Loading events…". Memoizing the object
// does not help; identity is not what is compared. Quantizing to whole local
// days is what makes the key stable.
//
// Whole days are also the right answer for a shared calendar: a 9:00 AM
// hearing should still be on today's list at 2:00 PM, which "from = this
// instant" got wrong.

import { MS_PER_DAY, endOfDay, startOfDay } from './date';

export type CalendarView = 'list' | 'month';

export interface CalendarRange {
  from: number;
  to: number;
}

/**
 * First day of the visible 6-week month grid: the Sunday on or before the
 * first of `anchor`'s month.
 */
export function startOfMonthGrid(anchor: Date): Date {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const out = new Date(first);
  out.setDate(first.getDate() - first.getDay());
  out.setHours(0, 0, 0, 0);
  return out;
}

/** Number of cells in the month grid — 6 weeks covers every month layout. */
export const MONTH_GRID_CELLS = 42;

/**
 * The [from, to] the calendar should request for the current controls.
 *
 * Month view depends only on `monthAnchor` and ignores `now` entirely. List
 * view spans whole local days: `rangeDays >= 0` looks forward from the start
 * of today, a negative `rangeDays` looks back and still ends at the end of
 * today.
 */
export function calendarRange(
  view: CalendarView,
  rangeDays: number,
  monthAnchor: Date,
  now: number,
): CalendarRange {
  if (view === 'month') {
    const gridStart = startOfMonthGrid(monthAnchor);
    const gridEnd = new Date(gridStart);
    gridEnd.setDate(gridStart.getDate() + MONTH_GRID_CELLS);
    return { from: gridStart.getTime(), to: gridEnd.getTime() };
  }

  const offset = now + rangeDays * MS_PER_DAY;
  return rangeDays >= 0
    ? { from: startOfDay(now), to: endOfDay(offset) }
    : { from: startOfDay(offset), to: endOfDay(now) };
}
